/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CardListSkeleton, DataState, Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { formatLocalizedDate } from '@/shared/utils/date';

import { useLaunchDashboard } from '../hooks/useLaunchDashboard';
import type { DashboardBroker } from '../types';
import { LaunchStatusDonut } from './LaunchStatusDonut';
import { ALLOCATION_ACTION_COLOR } from './launch-unit-status';

type FeedFilter = 'all' | 'allocation' | 'material';

interface LaunchBoardDashboardProps {
  agencyId: string;
  launchId: string;
  /**
   * `full` is the Painel tab, which owns the width and lays the three
   * blocks side by side. `rail` is the 340px column beside the units,
   * where three columns do not fit and would force the page to scroll
   * sideways.
   */
  layout?: 'full' | 'rail';
}

/**
 * Broker ranking, with bars rather than a bare table.
 *
 * Sold and held are stacked against the leader's sold count, so the gap
 * between first and fourth is visible without reading the numbers.
 */
function BrokerRanking({ brokers }: { brokers: DashboardBroker[] }) {
  const { t } = useTranslation('features/launches');
  if (brokers.length === 0) {
    return (
      <Text variant="small" as="span" className="text-[var(--text-tertiary)]">
        {t('dashboard.noBrokerActivity')}
      </Text>
    );
  }

  const top = Math.max(...brokers.map((b) => b.sold ?? 0), 1);

  return (
    <div className="flex flex-col gap-[var(--space-3)]">
      {brokers.map((broker, index) => (
        <div key={broker.broker_id} className="flex flex-col gap-[var(--space-1)]">
          <div className="flex items-baseline justify-between gap-[var(--space-2)]">
            <Text variant="bodySm" as="span" className="truncate text-[var(--text-primary)]">
              {index + 1}. {broker.broker_name || broker.broker_id}
            </Text>
            <Text variant="meta" as="span" className="shrink-0 tabular-nums">
              <span style={{ color: 'var(--launch-status-sold-fg)' }}>
                {t('dashboard.soldCount', { count: broker.sold ?? 0 })}
              </span>
              {(broker.held ?? 0) > 0 && (
                <span style={{ color: 'var(--launch-status-held-fg)' }}>
                  {' · '}
                  {t('dashboard.heldCount', { count: broker.held })}
                </span>
              )}
            </Text>
          </div>
          <div className="flex h-[var(--space-2)] overflow-hidden rounded-(--radius-full) bg-[var(--surface-tertiary)]">
            <span
              style={{
                width: `${((broker.sold ?? 0) / top) * 100}%`,
                background: 'var(--launch-status-sold-solid)',
              }}
            />
            <span
              style={{
                width: `${((broker.held ?? 0) / top) * 100}%`,
                background: 'var(--launch-status-held-solid)',
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The launch panel: how the inventory is doing, who is selling it, and what
 * just happened.
 *
 * The activity feed mixes allocation events with material ones, which is
 * why it carries a filter: during a live launch day the holds would bury
 * "book v2 published" otherwise.
 */
export function LaunchBoardDashboard({
  agencyId,
  launchId,
  layout = 'full',
}: LaunchBoardDashboardProps) {
  const { t } = useTranslation('features/launches');
  const { data, isLoading, isError, retry } = useLaunchDashboard(agencyId, launchId);
  const [feed, setFeed] = useState<FeedFilter>('all');

  const events = (data?.recent_activity ?? []).filter(
    (event) => feed === 'all' || (event.kind ?? 'allocation') === feed,
  );

  return (
    <section
      data-testid="launch-panel"
      className="flex flex-col gap-[var(--space-5)] rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-card)] p-[var(--space-4)]"
    >
      <DataState
        isLoading={isLoading}
        isError={isError}
        isEmpty={data === null && !isLoading}
        onRetry={retry}
        loadingFallback={<CardListSkeleton count={3} />}
      >
        {data && (
          /* Three columns from xl, as the prototype lays it out: the donut,
             the ranking and the feed answer three different questions and
             do not belong in one scroll. Stacked, the ranking bars
             stretched the full 1600px and the donut floated alone. */
          <div
            className={cn(
              'grid gap-[var(--space-5)]',
              layout === 'full'
                && 'xl:grid-cols-[minmax(240px,300px)_1fr_minmax(260px,340px)] xl:items-start',
            )}
          >
            <div className="flex flex-col items-center gap-[var(--space-2)]">
              <LaunchStatusDonut
                totals={data.totals ?? {
                  available: 0, held: 0, reserved_with_deposit: 0, sold: 0,
                }}
              />
            </div>

            <div className="flex flex-col gap-[var(--space-3)]">
              <Text
                as="span"
                className="text-[length:var(--text-app-heading)] [font-weight:var(--font-semibold)] text-[var(--text-primary)]"
              >
                {t('dashboard.brokerRanking')}
              </Text>
              <BrokerRanking brokers={data.top_brokers ?? []} />
            </div>

            <div className="flex flex-col gap-[var(--space-3)]">
              <Text
                as="span"
                className="text-[length:var(--text-app-heading)] [font-weight:var(--font-semibold)] text-[var(--text-primary)]"
              >
                {t('dashboard.activity')}
              </Text>

              <div className="flex gap-[var(--space-2)]">
                {(['all', 'allocation', 'material'] as const).map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFeed(key)}
                    className="min-h-[var(--space-12)] rounded-(--radius-full) border px-[var(--space-3)] text-[length:var(--text-meta)]"
                    style={{
                      background: feed === key ? 'var(--surface-inverse)' : 'var(--surface-primary)',
                      color: feed === key ? 'var(--text-inverse)' : 'var(--text-primary)',
                      borderColor: feed === key ? 'var(--surface-inverse)' : 'var(--border-medium)',
                    }}
                  >
                    {t(`dashboard.feed.${key}`)}
                  </button>
                ))}
              </div>

              {events.length === 0 ? (
                <Text variant="small" as="span" className="text-[var(--text-tertiary)]">
                  {t('dashboard.noActivity')}
                </Text>
              ) : (
                <div className="flex flex-col gap-[var(--space-3)]">
                  {events.slice(0, 12).map((event, index) => {
                    const isMaterial = (event.kind ?? 'allocation') === 'material';
                    return (
                      <div
                        key={`${event.unit_id ?? event.action}-${event.occurred_at}-${index}`}
                        className="flex items-start gap-[var(--space-3)]"
                      >
                        <span
                          className="mt-[var(--space-1)] inline-block h-2 w-2 shrink-0 rounded-full"
                          style={{
                            background: isMaterial
                              ? 'var(--text-tertiary)'
                              : (ALLOCATION_ACTION_COLOR[event.action] ?? 'var(--text-tertiary)'),
                          }}
                        />
                        <div className="flex min-w-0 flex-col">
                          <Text
                            as="span"
                            className="truncate text-[length:var(--text-small)]"
                            style={{
                              color: isMaterial ? 'var(--text-secondary)' : 'var(--text-primary)',
                              fontWeight: isMaterial ? undefined : 'var(--font-medium)',
                            }}
                          >
                            {t(`activityActions.${event.action}`, { defaultValue: event.action })}
                            {event.unit_label ? ` · ${event.unit_label}` : ''}
                          </Text>
                          <Text variant="meta" as="span" className="text-[var(--text-tertiary)]">
                            {formatLocalizedDate(event.occurred_at, undefined, {
                              day: '2-digit', month: '2-digit',
                              hour: '2-digit', minute: '2-digit',
                            })}
                          </Text>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </DataState>
    </section>
  );
}
